import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup,
} from '@mui/material';
import { adminAuthAPI } from '../services/api';
import { roleLabel } from '../utils/roleAccess';

// Users added to several tenants pick the one they work in (same idea as ReachOut's
// "Choose a tenant to continue"). Data, roles and permissions all follow the choice.

const CONFIRMED_KEY = 'wz_tenant_confirmed';

export function tenantChoiceConfirmed(tenantId) {
  try {
    return !!tenantId && sessionStorage.getItem(CONFIRMED_KEY) === tenantId;
  } catch {
    return true;
  }
}

export function clearTenantChoice() {
  try { sessionStorage.removeItem(CONFIRMED_KEY); } catch { /* ignore */ }
}

function errorText(err) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not switch tenant. Please try again.';
}

/** `forced`: first choice after sign-in (no Cancel). Otherwise opened from "Switch". */
export default function TenantSelectModal({ open, tenants, currentId, forced, onClose }) {
  const [selected, setSelected] = useState(currentId || tenants[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setSelected(currentId || tenants[0]?.id || ''); setError(''); } }, [open, currentId, tenants]);

  const choose = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      // Always ask the server, even for the current tenant: it returns a session token
      // pinned to this tenant (Microsoft sign-in tokens carry no tenant of their own).
      const res = await adminAuthAPI.selectTenant(selected);
      const next = res.data?.admin || {};
      try {
        const prev = JSON.parse(localStorage.getItem('admin') || '{}');
        localStorage.setItem('admin', JSON.stringify({ ...prev, ...next }));
        // The new session token is pinned to the chosen tenant for this browser only,
        // so the same person can work in another tenant in a different browser.
        if (res.data?.access_token) localStorage.setItem('token', res.data.access_token);
      } catch { /* ignore */ }
      try { sessionStorage.setItem(CONFIRMED_KEY, selected); } catch { /* ignore */ }
      // Reload so every page, list and permission shows the chosen tenant.
      window.location.reload();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={forced ? undefined : onClose} fullWidth maxWidth="xs" disableEscapeKeyDown={forced}>
      <DialogTitle>{forced ? 'Choose a tenant to continue' : 'Switch tenant'}</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 0 }}>
          You have access to more than one company. Pick the one you want to work in — you can switch any time from the sidebar.
        </p>
        <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
          {tenants.map((t) => (
            <FormControlLabel
              key={t.id}
              value={t.id}
              control={<Radio size="small" />}
              label={(
                <span style={{ fontSize: 14 }}>
                  <b>{t.name}</b>
                  {Array.isArray(t.roles) && t.roles.length > 0 && (
                    <span style={{ color: '#6B7280', fontSize: 12 }}> · {t.roles.map(roleLabel).filter(Boolean).join(' + ')}</span>
                  )}
                  {t.id === currentId && <span style={{ color: '#6366F1', fontSize: 12 }}> · current</span>}
                </span>
              )}
            />
          ))}
        </RadioGroup>
        {error && <div role="alert" style={{ color: '#DC2626', fontSize: 12, marginTop: 8 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        {!forced && <Button onClick={onClose} disabled={busy}>Cancel</Button>}
        <Button variant="contained" onClick={choose} disabled={busy || !selected}>
          {busy ? 'Switching…' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
